import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Building2, Crosshair, Search, Wallet } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, EmptyState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  getAdvertiser,
  creditAdvertiser,
  getCampaign,
  getTargeting,
  setTargeting,
  ADSD_STR as S,
  type AdvertiserDetail,
  type CampaignDetail,
  type Targeting,
} from './adsDetailApi'

/* ── shared: ID kiritish + yuklash (list-siz standalone kirish) ──── */
export function useEntity<T>(fetcher: (id: number, s: AbortSignal) => Promise<T>, initialId?: string) {
  const [idInput, setIdInput] = useState(initialId ?? '')
  const [id, setId] = useState<number | null>(initialId ? Number(initialId) || null : null)
  const [data, setData] = useState<T | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [loading, setLoading] = useState(false)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (id === null || id <= 0) return
    const ac = new AbortController()
    setLoading(true)
    setErr(null)
    fetcher(id, ac.signal)
      .then((d) => setData(d))
      .catch((e) => {
        if (ac.signal.aborted) return
        setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
        setData(null)
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false)
      })
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tick])

  const submit = useCallback(() => {
    const n = Number(idInput.trim())
    setId(n > 0 ? n : null)
  }, [idInput])
  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { idInput, setIdInput, id, submit, data, err, loading, reload, setData }
}

export function IdBar({
  value,
  onChange,
  onSubmit,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  onSubmit: () => void
  placeholder: string
}) {
  return (
    <div className="mb-4 flex max-w-md gap-2">
      <input
        value={value}
        inputMode="numeric"
        onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ''))}
        onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
        placeholder={placeholder}
        className="flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={!value.trim()}
        className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50"
      >
        <Search size={14} />
        {S.load}
      </button>
    </div>
  )
}

export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-border-subtle py-2 first:border-t-0">
      <span className="text-xs text-tertiary">{label}</span>
      <span className="text-right text-sm text-primary">{children}</span>
    </div>
  )
}

export function LoadingCard() {
  return (
    <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-4 w-2/3" />
      ))}
    </div>
  )
}

/* ── A20.02 — Reklamachi kartasi ─────────────────────────────────── */
export function AdvertiserCardScreen() {
  const { id: paramId } = useParams()
  const { idInput, setIdInput, id, submit, data, err, loading, reload } = useEntity<AdvertiserDetail>(
    getAdvertiser,
    paramId,
  )
  return (
    <>
      <PageHeader title={S.advertiserCard} screenId="A20.02" />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {!paramId && (
          <IdBar value={idInput} onChange={setIdInput} onSubmit={submit} placeholder={S.advertiserId} />
        )}
        {id === null ? (
          <EmptyState icon={<Building2 size={28} strokeWidth={1.5} />} title={S.enterId} />
        ) : err ? (
          <ErrorState code={err.code} title={err.status === 404 ? S.notFound : undefined} onRetry={reload} />
        ) : loading || !data ? (
          <LoadingCard />
        ) : (
          <div className="max-w-3xl space-y-4">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-primary">{data.name}</h2>
                <span className="rounded-full border border-border-default px-2 py-0.5 text-xs text-secondary">
                  {data.status}
                </span>
              </div>
              <Row label={S.balance}>
                <span className="tabular font-medium">{data.balance.toLocaleString()}</span>
              </Row>
              <Row label={S.spent}>
                <span className="tabular">{data.spent.toLocaleString()}</span>
              </Row>
              <Row label={S.campaigns}>{data.campaigns}</Row>
              <Row label={S.created}>{data.created_at}</Row>
            </div>

            <div className="overflow-hidden rounded-lg border border-border-default">
              <p className="border-b border-border-subtle bg-surface-2 px-4 py-2 text-xs font-medium text-secondary">
                {S.campaignsCol}
              </p>
              {data.campaign_list.length === 0 ? (
                <EmptyState title={S.empty} />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-surface-2 text-xs text-tertiary">
                      <tr>
                        <th className="px-4 py-2 font-medium">{S.title}</th>
                        <th className="px-4 py-2 font-medium">{S.status}</th>
                        <th className="px-4 py-2 text-right font-medium">{S.budget}</th>
                        <th className="px-4 py-2 text-right font-medium">{S.spent}</th>
                        <th className="px-4 py-2 text-right font-medium">{S.cpm}</th>
                        <th className="px-4 py-2 text-right font-medium">{S.impressions}</th>
                        <th className="px-4 py-2 text-right font-medium">{S.clicks}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.campaign_list.map((c) => (
                        <tr key={c.id} className="border-t border-border-subtle">
                          <td className="px-4 py-2 text-primary">{c.title}</td>
                          <td className="px-4 py-2 text-secondary">{c.status}</td>
                          <td className="px-4 py-2 text-right tabular text-secondary">{c.budget.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right tabular text-secondary">{c.spent.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right tabular text-secondary">{c.cpm.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right tabular text-secondary">
                            {c.impressions.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-right tabular text-secondary">{c.clicks.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A20.03 — Balans va to'lovlar ───────────────────────────────── */
export function AdvertiserBalanceScreen() {
  const { toast } = useToast()
  const { idInput, setIdInput, id, submit, data, err, loading, reload } =
    useEntity<AdvertiserDetail>(getAdvertiser)
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)

  const topup = async () => {
    if (id === null) return
    const n = Number(amount.trim())
    if (!n || n <= 0) return
    setBusy(true)
    try {
      await creditAdvertiser(id, n)
      toast(S.topupOk, { tone: 'success' })
      setAmount('')
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.balanceTitle} screenId="A20.03" description={S.balanceDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <IdBar value={idInput} onChange={setIdInput} onSubmit={submit} placeholder={S.advertiserId} />
        {id === null ? (
          <EmptyState icon={<Wallet size={28} strokeWidth={1.5} />} title={S.enterId} />
        ) : err ? (
          <ErrorState code={err.code} title={err.status === 404 ? S.notFound : undefined} onRetry={reload} />
        ) : loading || !data ? (
          <LoadingCard />
        ) : (
          <div className="max-w-xl space-y-4">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-base font-semibold text-primary">{data.name}</h2>
                <span className="rounded-full border border-border-default px-2 py-0.5 text-xs text-secondary">
                  {data.status}
                </span>
              </div>
              <Row label={S.available}>
                <span className="tabular text-lg font-semibold text-primary">{data.balance.toLocaleString()}</span>
              </Row>
              <Row label={S.spent}>
                <span className="tabular">{data.spent.toLocaleString()}</span>
              </Row>
            </div>

            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <label className="mb-1 block text-xs text-tertiary">{S.amount}</label>
              <div className="flex gap-2">
                <input
                  value={amount}
                  inputMode="numeric"
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && topup()}
                  placeholder="0"
                  className="flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
                />
                <button
                  type="button"
                  onClick={topup}
                  disabled={busy || !amount.trim()}
                  className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50"
                >
                  <Wallet size={14} />
                  {S.topup}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-tertiary">{S.amountHint}</p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A20.07 — Targeting sozlamalari ─────────────────────────────── */
export function TargetingScreen() {
  const { toast } = useToast()
  // Kampaniya kartasi (sarlavha) — GET /admin/ads/campaigns/:id (orphan endpoint).
  const camp = useEntity<CampaignDetail>(getCampaign)
  const [tgt, setTgt] = useState<Targeting | null>(null)
  const [busy, setBusy] = useState(false)

  // Kampaniya yuklangач targetingни ham tortadi.
  useEffect(() => {
    if (camp.id === null || camp.id <= 0) {
      setTgt(null)
      return
    }
    const ac = new AbortController()
    getTargeting(camp.id, ac.signal)
      .then((t) => setTgt(t))
      .catch(() => {
        /* fail-soft: default targeting bilan qoladi */
      })
    return () => ac.abort()
  }, [camp.id, camp.data])

  const save = async () => {
    if (camp.id === null || !tgt) return
    setBusy(true)
    try {
      await setTargeting(camp.id, {
        countries: tgt.countries,
        platforms: tgt.platforms,
        min_age: tgt.min_age,
        max_age: tgt.max_age,
      })
      toast(S.saved, { tone: 'success' })
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const patch = (p: Partial<Targeting>) => setTgt((prev) => (prev ? { ...prev, ...p } : prev))

  return (
    <>
      <PageHeader title={S.targeting} screenId="A20.07" description={S.targetingDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <IdBar value={camp.idInput} onChange={camp.setIdInput} onSubmit={camp.submit} placeholder={S.campaignId} />
        {camp.id === null ? (
          <EmptyState icon={<Crosshair size={28} strokeWidth={1.5} />} title={S.enterId} />
        ) : camp.err ? (
          <ErrorState
            code={camp.err.code}
            title={camp.err.status === 404 ? S.notFound : undefined}
            onRetry={camp.reload}
          />
        ) : camp.loading || !camp.data || !tgt ? (
          <LoadingCard />
        ) : (
          <div className="max-w-xl space-y-4">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-base font-semibold text-primary">{camp.data.title}</h2>
                <span className="rounded-full border border-border-default px-2 py-0.5 text-xs text-secondary">
                  {camp.data.status}
                </span>
              </div>
              <Row label={S.advertiser}>{camp.data.advertiser}</Row>
              <Row label={S.cpm}>
                <span className="tabular">{camp.data.cpm.toLocaleString()}</span>
              </Row>
            </div>

            <div className="space-y-3 rounded-lg border border-border-default bg-surface-2 p-4">
              <div>
                <label className="mb-1 block text-xs text-tertiary">{S.countries}</label>
                <input
                  value={tgt.countries}
                  onChange={(e) => patch({ countries: e.target.value })}
                  placeholder="UZ,RU,KZ"
                  className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-tertiary">{S.platforms}</label>
                <input
                  value={tgt.platforms}
                  onChange={(e) => patch({ platforms: e.target.value })}
                  placeholder="android,ios,web"
                  className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="mb-1 block text-xs text-tertiary">{S.minAge}</label>
                  <input
                    value={tgt.min_age || ''}
                    inputMode="numeric"
                    onChange={(e) => patch({ min_age: Number(e.target.value.replace(/[^\d]/g, '')) || 0 })}
                    className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
                  />
                </div>
                <div className="flex-1">
                  <label className="mb-1 block text-xs text-tertiary">{S.maxAge}</label>
                  <input
                    value={tgt.max_age || ''}
                    inputMode="numeric"
                    onChange={(e) => patch({ max_age: Number(e.target.value.replace(/[^\d]/g, '')) || 0 })}
                    className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
                  />
                </div>
              </div>
              <p className="text-xs text-tertiary">{S.targetingHint}</p>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50"
              >
                {S.save}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
